/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import l10n from '../../shared/strings';
import { findListingEntity, getListingProductIdFromState } from './helpers/entityReadHelpers';
import { upsertListing } from './helpers/entityAdapter';

const strings = l10n.ACTIONS_TOOLBAR.PRODUCTS;

const patchSearchItemArchived = (item) => {
  item.status = 'ARCHIVED';
  if (item.live) {
    item.live.status = 'ARCHIVED';
  }
};

export const archiveListing = createAsyncThunk(
  'products/archiveListing',
  async ({ masterProductId, channelId, channelName }, {
    getState,
    extra: { sdk, Logger, Toast },
  }) => {
    const listingProductId = getListingProductIdFromState(getState().products, masterProductId, channelId);

    if (!listingProductId) {
      throw new Error(`Listing not found for channel ${channelId}`);
    }

    try {
      await sdk.products.archiveListing(listingProductId);
      if (channelName) {
        Toast(strings.archiveProductSucceeded(channelName), { type: 'success' });
      }
      return { masterProductId, channelId };
    } catch (error) {
      Toast('Listing is not archived. Refresh and try again', { autoClose: false, type: 'error' });
      Logger.error(new Error('Error archiving product'), { error }, [
        'ArchiveListingFailed',
        { errorMessage: 'Listing is not archived on the backend.' },
      ]);
      throw error;
    }
  },
);

export const reducer = (builder) => {
  builder
    .addCase(archiveListing.pending, (state) => {
      state.isBusy = true;
      state.error = null;
    })
    .addCase(archiveListing.fulfilled, (state, { payload }) => {
      const { masterProductId, channelId } = payload;
      state.isBusy = false;

      const existingListing = findListingEntity(state, { masterProductId, channelId });
      if (!existingListing?._id) return;

      upsertListing(state, {
        ...existingListing,
        masterProductId,
        status: 'ARCHIVED',
        live: existingListing.live
          ? { ...existingListing.live, status: 'ARCHIVED' }
          : null,
      });

      const listingId = String(existingListing._id);
      if (!Array.isArray(state.search?.items)) return;

      state.search.items.forEach((item) => {
        if (!item) return;
        const isListing = item.type === 'listing' && String(item._id) === listingId;
        const isListingVariant = item.type === 'listingVariant'
          && String(item.baseProductId) === listingId;
        if (!isListing && !isListingVariant) return;
        patchSearchItemArchived(item);
      });
    })
    .addCase(archiveListing.rejected, (state, { error }) => {
      state.isBusy = false;
      state.error = error?.message || 'Cannot archive product';
    });
};
